import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Star, Clock, X, CalendarPlus, Crown, Heart, MessageSquare, Loader2, Globe, Tag } from "lucide-react";
import { toast } from "sonner";

const TIMEZONES = ["UTC", "America/Los_Angeles", "America/New_York", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Tokyo", "Australia/Sydney"];

function CoachModal({ coach, onClose }) {
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tz, setTz] = useState("UTC");
  const [selected, setSelected] = useState(null);
  const [discipline, setDiscipline] = useState(coach.specialties?.[0] || "Personal Training");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/instructors/${coach.user_id}/availability`).then((r) => setSlots(r.data.slots)).catch(() => {});
    api.get(`/payments/coupons?instructor_id=${coach.user_id}`).then((r) => setCoupons(r.data)).catch(() => {});
    api.get(`/instructors/${coach.user_id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [coach]);

  const fmtSlot = (iso) => {
    try {
      return new Intl.DateTimeFormat("en-US", { weekday: "short", hour: "2-digit", minute: "2-digit", timeZone: tz }).format(new Date(iso));
    } catch { return new Date(iso).toLocaleString(); }
  };

  const book = async () => {
    if (!selected) return toast.error("Pick a time slot");
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", {
        item_type: "session", instructor_id: coach.user_id,
        start_time: selected, discipline, timezone: tz,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch { toast.error("Could not start checkout"); setBusy(false); }
  };

  const subscribe = async () => {
    const res = await api.post("/payments/checkout", { item_type: "subscription", instructor_id: coach.user_id, origin_url: window.location.origin });
    window.location.href = res.data.checkout_url;
  };
  const tip = async () => {
    const res = await api.post("/payments/checkout", { item_type: "tip", instructor_id: coach.user_id, tip_tier: "medium", origin_url: window.location.origin });
    window.location.href = res.data.checkout_url;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()} data-testid="coach-modal">
        <div className="relative h-40">
          <img src={coach.picture} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E1E] to-transparent" />
          <button data-testid="close-coach-modal" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 -mt-10 relative">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight text-white">{coach.name}</h2>
          <div className="flex items-center gap-3 mt-1 mb-2 text-sm">
            <span className="flex items-center gap-1 text-[#FF6B6B]"><Star className="w-4 h-4 fill-[#FF6B6B]" /> {coach.rating}</span>
            <span className="text-[#A0A0A0]">({coach.review_count || reviews.length} reviews)</span>
            <span className="text-[#A0A0A0] flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {coach.timezone}</span>
            <span className="text-white font-semibold">${coach.hourly_rate}/hr</span>
          </div>
          <p className="text-sm text-[#A0A0A0] mb-3">{coach.bio}</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {(coach.specialties || []).map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B]">{s}</span>)}
          </div>

          {coupons.length > 0 && (
            <div className="mb-5 p-3 rounded-xl bg-[#FF6B6B]/5 border border-[#FF6B6B]/20" data-testid="coach-coupons">
              <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2 flex items-center gap-1"><Tag className="w-3.5 h-3.5 text-[#FF6B6B]" /> Discount codes — apply at checkout</p>
              <div className="flex flex-wrap gap-2">
                {coupons.map((c) => (
                  <button key={c.coupon_id} data-testid={`copy-coupon-${c.code}`} onClick={() => { navigator.clipboard?.writeText(c.code); toast.success(`Code ${c.code} copied — paste it at checkout`); }} className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FF6B6B] text-black hover:brightness-110 transition-all">
                    {c.code} · {c.percent_off}% OFF
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-5">
            <button data-testid="subscribe-btn" onClick={subscribe} className="flex-1 py-2.5 rounded-xl bg-[#4ECCD3]/10 border border-[#4ECCD3]/40 text-[#4ECCD3] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#4ECCD3]/20 transition-colors"><Crown className="w-4 h-4" /> VIP $29/mo</button>
            <button data-testid="tip-btn" onClick={tip} className="py-2.5 px-4 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#333333] transition-colors"><Heart className="w-4 h-4 text-[#FF3B30]" /> Tip</button>
            <button data-testid="message-coach-btn" onClick={() => navigate(`/messages?to=${coach.user_id}`)} className="py-2.5 px-4 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#333333] transition-colors"><MessageSquare className="w-4 h-4" /></button>
          </div>

          <div className="border-t border-[#2A2A2A] pt-5">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-[#FF6B6B]" /> Book 1-on-1</h3>
            <div className="flex gap-2 mb-3">
              <input data-testid="discipline-input" value={discipline} onChange={(e) => setDiscipline(e.target.value)} placeholder="Discipline" className="flex-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
              <select data-testid="timezone-select" value={tz} onChange={(e) => { setTz(e.target.value); setSelected(null); }} className="bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]">
                {TIMEZONES.map((t) => <option key={t} value={t}>{t.split("/").pop().replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto no-scrollbar mb-4">
              {slots.slice(0, 15).map((s) => (
                <button key={s} data-testid="slot-btn" onClick={() => setSelected(s)} className={`py-2 rounded-lg text-xs font-medium border flex items-center justify-center gap-1 transition-colors ${selected === s ? "border-[#FF6B6B] bg-[#FF6B6B]/10 text-[#FF6B6B]" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
                  <Clock className="w-3 h-3" /> {fmtSlot(s)}
                </button>
              ))}
            </div>
            <button data-testid="confirm-booking-btn" onClick={book} disabled={busy} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm hover:brightness-110 transition-all disabled:opacity-50">
              {busy ? "Booking…" : `Book Session · $${coach.hourly_rate}`}
            </button>
            <p className="text-xs text-[#A0A0A0] text-center mt-2">Secure Stripe checkout · email confirmation + .ics calendar invite</p>
          </div>

          {reviews.length > 0 && (
            <div className="border-t border-[#2A2A2A] pt-5 mt-5" data-testid="coach-reviews">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-[#FF6B6B] fill-[#FF6B6B]" /> Reviews</h3>
              <div className="space-y-3 max-h-56 overflow-y-auto no-scrollbar">
                {reviews.map((rv) => (
                  <div key={rv.review_id} className="bg-[#121212] rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-white">{rv.learner_name}</span>
                      <span className="flex items-center gap-0.5 text-[#FF6B6B] text-xs">
                        {Array.from({ length: rv.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-[#FF6B6B]" />)}
                      </span>
                    </div>
                    <p className="text-xs text-[#A0A0A0]">{rv.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Discover() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState("coaches");
  const [coaches, setCoaches] = useState([]);
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const q = (params.get("q") || "").toLowerCase();

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.get("/instructors"), api.get("/plans")]);
    setCoaches(c.data); setPlans(p.data); setLoading(false);
    const bookId = params.get("book");
    if (bookId) {
      const found = c.data.find((x) => x.user_id === bookId);
      if (found) setActive(found);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const purchase = async (plan) => {
    const res = await api.post("/payments/checkout", { item_type: "product", item_id: plan.plan_id, origin_url: window.location.origin });
    window.location.href = res.data.checkout_url;
  };

  const fc = coaches.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.specialties || []).join(" ").toLowerCase().includes(q));
  const fp = plans.filter((p) => !q || p.title.toLowerCase().includes(q) || p.type.includes(q));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin" /></div>;

  return (
    <div>
      <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white mb-1">Discover</h1>
      <p className="text-sm text-[#A0A0A0] mb-6">World-class coaches and programs, ready when you are.</p>

      <div className="flex gap-2 mb-6">
        {["coaches", "plans"].map((t) => (
          <button key={t} data-testid={`discover-tab-${t}`} onClick={() => setTab(t)} className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-[#FF6B6B] text-black" : "bg-[#1E1E1E] text-[#A0A0A0] border border-[#2A2A2A] hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "coaches" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fc.map((c) => (
            <button key={c.user_id} data-testid={`coach-card-${c.user_id}`} onClick={() => setActive(c)} className="text-left bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-[#FF6B6B]/50 transition-all duration-200 group">
              <div className="h-44 overflow-hidden relative">
                <img src={c.picture} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E1E] to-transparent" />
                <span className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-xs text-[#FF6B6B]"><Star className="w-3 h-3 fill-[#FF6B6B]" /> {c.rating} <span className="text-white/70">· {c.review_count || 0}</span></span>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white">{c.name}</h3>
                <p className="text-xs text-[#A0A0A0] line-clamp-2 mt-1 mb-3">{c.bio}</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(c.specialties || []).slice(0, 3).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B]">{s}</span>)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">${c.hourly_rate}/hr</span>
                  <span className="text-xs text-[#FF6B6B] font-semibold">Book →</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {tab === "plans" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fp.map((p) => (
            <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`} className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-[#FF6B6B]/50 transition-all duration-200 group">
              <div className="h-40 overflow-hidden"><img src={p.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>
              <div className="p-4">
                <span className="text-[10px] uppercase tracking-wider text-[#FF6B6B]">{p.type}</span>
                <h3 className="font-bold text-white line-clamp-1">{p.title}</h3>
                <p className="text-xs text-[#A0A0A0] line-clamp-2 mt-1 mb-3">{p.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-xl text-white">${p.price}</span>
                  <button data-testid={`purchase-plan-${p.plan_id}`} onClick={() => purchase(p)} className="px-4 py-2 rounded-full bg-[#FF6B6B] text-black text-xs font-bold uppercase tracking-wide hover:brightness-110 transition-all">Buy</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && <CoachModal coach={active} onClose={() => setActive(null)} />}
    </div>
  );
}
